import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, CircularProgress, Alert,
  TextField, Chip,
} from '@mui/material';
import FlexService from '../../services/FlexService';
import EntityWizardOverlay from './EntityWizardOverlay';

// An inspection or a work order (a wizard run, FH-363/364), one step at a time. Each step's form is the entity form
// the rest of the web app uses (EntityWizardOverlay, embedded): the step names its entity and fields, and the field
// metadata says the labels, what is required, the choices and when a field shows. Next saves the step on the server
// (wizard_run_submit, or wizard_run_approve for a supervisor's step), which answers with the run where it now stands;
// Back reopens the question before while nothing was applied since.
// Opened by a WIZARD_RUN row action: target {startScript, payload} starts a run, {run} opens one.

const call = async (scriptName, payload) => {
  const resp = await FlexService.executeScript({ scriptName, context: { _payload: payload } });
  const ctx = resp?.context || {};
  if (resp?.success === false || ctx.error_message) {
    throw new Error(ctx.error_message || resp?.errorMessage || 'The warehouse system did not answer.');
  }
  return ctx.result;
};

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '');

const OUTCOME_COLOR = { READY: 'success', REPAIR: 'error', QUARANTINE: 'warning', RETIRED: 'default' };

export default function WizardRunPanel({ target, colors = {}, onClose }) {
  const open = !!target;
  const [run, setRun] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const show = useCallback((r) => {
    setRun(r);
    setError(r?.status === 'FAILED' ? (r.errorMessage || 'A step did not finish.') : '');
  }, []);

  const load = useCallback(async (t) => {
    if (!t) return;
    setBusy(true);
    setError('');
    try {
      show(t.run ? await call('wizard_run_get', { run: t.run }) : await call(t.startScript, t.payload || {}));
    } catch (e) {
      setRun(null);
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [show]);

  useEffect(() => {
    setRun(null);
    setCancelling(false);
    setCancelReason('');
    load(target);
  }, [target, load]);

  const act = async (scriptName, payload) => {
    setBusy(true);
    setError('');
    try {
      show(await call(scriptName, { run: run.runNumber, ...payload }));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const step = run?.status === 'WAITING_INPUT' ? run.currentStep : null;
  const values = run?.values || {};
  const result = run?.result || {};
  const subject = [values.itemDescription || values.itemId, values.serialNumber ? `serial ${values.serialNumber}` : null,
    run?.subjectId ? `LPN ${run.subjectId}` : null].filter(Boolean).join(' · ');
  const finished = run && (run.status === 'COMPLETED' || run.status === 'CANCELLED');

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md"
      PaperProps={{ sx: { borderRadius: 0, bgcolor: colors.surface || colors.background } }}>
      <DialogTitle sx={{ color: colors.textPrimary, pb: 0.5 }}>
        {run ? `${run.runNumber} · ${run.title}` : 'Starting…'}
        {run ? (
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            {subject}{subject ? ' · ' : ''}{run.statusLabel}{run.startedBy ? ` · started by ${run.startedBy}` : ''}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        {error ? <Alert severity="error" sx={{ my: 1, borderRadius: 0 }}>{error}</Alert> : null}
        {!run && busy ? <CircularProgress size={24} sx={{ mt: 2 }} /> : null}
        {run ? (
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' }, mt: 1 }}>
            <Box component="ol" aria-label="Steps" sx={{ m: 0, pl: 2.5, minWidth: { sm: 200 }, color: colors.textSecondary }}>
              {(run.steps || []).map((s) => (
                <Typography component="li" variant="body2" key={s.key}
                  sx={{ fontWeight: s.state === 'CURRENT' ? 700 : 400, color: s.state === 'CURRENT' ? colors.textPrimary : undefined, py: 0.25 }}>
                  {s.title}{s.state === 'DONE' ? ' ✓' : ''}
                </Typography>
              ))}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              {step ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="overline" sx={{ color: colors.textSecondary }}>
                      Step {run.stepNumber} of {run.totalSteps}
                    </Typography>
                    {step.check?.critical ? <Chip size="small" label="Critical" color="error" variant="outlined" sx={{ borderRadius: 0 }} /> : null}
                    {step.approval ? <Chip size="small" label="Supervisor" variant="outlined" sx={{ borderRadius: 0 }} /> : null}
                  </Box>
                  {step.copy ? <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>{step.copy}</Typography> : null}
                  {step.entity ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 320, border: `1px solid ${colors.border || '#ccc'}` }}>
                      <EntityWizardOverlay
                        key={`${run.runNumber}:${step.key}:${run.stepNumber}`}
                        embedded
                        forceCreate
                        entityName={step.entity}
                        fieldNames={step.fieldNames}
                        rowData={step.values || {}}
                        title={step.title}
                        saveLabel={step.ctaLabel || 'Next'}
                        scriptName={step.approval ? 'wizard_run_approve' : 'wizard_run_submit'}
                        buildPayload={(answers) => ({ run: run.runNumber, stepKey: step.key, answers })}
                        colors={colors}
                        onSave={(next) => show(next)}
                      />
                    </Box>
                  ) : (
                    <Alert severity="warning" sx={{ borderRadius: 0 }}>
                      This step names no form entity, so it cannot be shown here. Its wizard needs an entity and fields.
                    </Alert>
                  )}
                </>
              ) : null}
              {run.status === 'COMPLETED' ? (
                <Box>
                  <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 1 }}>
                    {result.outcomeLabel ? (
                      <Chip label={result.outcomeLabel} color={OUTCOME_COLOR[result.outcome] || 'default'} sx={{ borderRadius: 0, mr: 1 }} />
                    ) : null}
                    {result.summary || 'Completed'}
                  </Typography>
                  {result.failedChecks ? <Typography variant="body2" sx={{ color: colors.textSecondary }}>Failed: {result.failedChecks}</Typography> : null}
                  {result.inventoryStatus ? <Typography variant="body2" sx={{ color: colors.textSecondary }}>Status now: {result.inventoryStatusLabel || result.inventoryStatus}</Typography> : null}
                  {result.nextInspection ? <Typography variant="body2" sx={{ color: colors.textSecondary }}>Next inspection: {result.nextInspection}</Typography> : null}
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>Completed {when(run.completedAt)}</Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    {result.workOrder ? <Button variant="outlined" sx={{ borderRadius: 0 }} onClick={() => load({ run: result.workOrder })}>Open work order {result.workOrder}</Button> : null}
                    {result.reinspection ? <Button variant="outlined" sx={{ borderRadius: 0 }} onClick={() => load({ run: result.reinspection })}>Open reinspection {result.reinspection}</Button> : null}
                  </Box>
                </Box>
              ) : null}
              {run.status === 'CANCELLED' ? (
                <Typography sx={{ color: colors.textSecondary }}>{run.errorMessage || 'Cancelled.'}</Typography>
              ) : null}
              {cancelling ? (
                <Box sx={{ mt: 2 }}>
                  <TextField fullWidth autoFocus label="Why cancel? *" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                    helperText="Steps already applied (such as a status change) stay as they are." />
                </Box>
              ) : null}
            </Box>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        {run && run.canCancel && !cancelling ? <Button color="error" disabled={busy} onClick={() => setCancelling(true)}>Cancel {run.runNumber}</Button> : null}
        {cancelling ? (
          <>
            <Button disabled={busy} onClick={() => setCancelling(false)}>Keep it</Button>
            <Button color="error" variant="contained" sx={{ borderRadius: 0 }} disabled={busy || !cancelReason.trim()}
              onClick={async () => { await act('wizard_run_cancel', { reason: cancelReason.trim() }); setCancelling(false); }}>
              Cancel {run?.runNumber}
            </Button>
          </>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {run?.canBack && !cancelling ? <Button disabled={busy} onClick={() => act('wizard_run_back', {})}>Back</Button> : null}
        {run?.canRetry && !cancelling ? <Button variant="contained" sx={{ borderRadius: 0 }} disabled={busy} onClick={() => act('wizard_run_retry', {})}>Try again</Button> : null}
        {finished || !run ? <Button variant={finished ? 'contained' : 'text'} sx={{ borderRadius: 0 }} disabled={busy} onClick={onClose}>Close</Button> : null}
        {run && !finished && !cancelling ? <Button disabled={busy} onClick={onClose}>Finish later</Button> : null}
      </DialogActions>
    </Dialog>
  );
}
